import { useState, useMemo, useEffect, useCallback } from 'react';
import { toastr } from 'react-redux-toastr';
import {
  Stack,
  Typography,
  Divider,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Banner,
  Modal,
  Grid,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  PeopleIcon,
  CloseIcon,
  AlertIcon,
  LessThanIcon,
  GreaterThanIcon,
  AlertInfoIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import messages from './messages';
import DuplicatesForm from './DuplicatesForm';
import ConfirmRemovalModal from './ConfirmRemovalModal';
import ConfirmMergeModal from './ConfirmMergeModal';
import { validateMergeForm, formatValidationErrors } from './validation';

// Default values for legacy prospect data
const DEFAULT_EMAIL_TYPE = 'personal';
const DEFAULT_PHONE_TYPE = 'mobile';

type Props = {
  open: boolean,
  onClose: () => void,
  duplicatesData: Array<any>,
  intl: Object,
  onNotAMatch: (id: string) => void,
  onMerge: (id: string, mergeData: Object) => void,
};

const DuplicatesModal = ({
  open,
  onClose,
  duplicatesData,
  intl,
  onNotAMatch,
  onMerge,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
  const [formRef, setFormRef] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isMergeButtonEnabled, setIsMergeButtonEnabled] = useState(false);

  const handleNextPage = () => {
    if (currentPage < duplicatesData.length - 1) {
      setCurrentPage(currentPage + 1);
      setSelectedProfileId(null); // Reset profile selection on page change
      setValidationError(null); // Clear validation error on page change
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      setSelectedProfileId(null); // Reset profile selection on page change
      setValidationError(null); // Clear validation error on page change
    }
  };

  const handleProfileSelect = (prospectId) => {
    setSelectedProfileId(prospectId);
    setValidationError(null); // Clear validation error when profile is selected
  };

  const handleNotAMatch = () => {
    if (!currentDuplicateData?.id) {
      toastr.error('Error', 'No duplicate record selected');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmRemoval = () => {
    setShowConfirmModal(false);
    onNotAMatch(currentDuplicateData.id);
    onClose();
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleConfirm = () => {
    if (!currentDuplicateData?.id) {
      toastr.error('Error', 'No duplicate record selected');
      return;
    }

    if (!formRef) {
      toastr.error('Error', 'Form data not available');
      return;
    }

    // Validate profile selection first
    if (!selectedProfileId) {
      setValidationError(intl.formatMessage(messages.selectProfileRequired));
      return;
    }

    // Validate form before showing confirmation
    const formData = formRef.getValues();
    const validation = validateMergeForm(
      formData,
      formKey,
      primaryProfile,
      duplicateProfile,
      selectedProfileId,
    );

    if (!validation.isValid) {
      setValidationError(formatValidationErrors(validation.errors));
      return;
    }

    setShowMergeConfirmModal(true);
  };

  // Extract the actual values of selected fields
  const extractSelectedValues = (formData) => {
    const selected = {
      firstName: null,
      lastName: null,
      preferredName: null,
      emails: [],
      phones: [],
    };

    // Helper to check if name is selected (handles both unique and shared field names)
    const isNameSelected = (name) => {
      const sanitizedName = name.replace(/[^a-zA-Z0-9]/g, '_');
      const sharedFieldName = `${formKey}_shared_name_${sanitizedName}`;
      return formData[sharedFieldName];
    };

    // Extract selected name (exclusive checkboxes) - preserve firstName and lastName
    const primaryNameSelected =
      formData[`${formKey}_primaryName`] || isNameSelected(primaryProfile.name);
    const duplicateNameSelected =
      formData[`${formKey}_duplicateName`] ||
      isNameSelected(duplicateProfile.name);

    if (primaryNameSelected) {
      const mainProspect = currentDuplicateData.mainProspectData;
      selected.firstName = mainProspect?.firstName || '';
      selected.lastName = mainProspect?.lastName || '';
    } else if (duplicateNameSelected) {
      const duplicateProspect = currentDuplicateData.duplicateProspectData;
      selected.firstName = duplicateProspect?.firstName || '';
      selected.lastName = duplicateProspect?.lastName || '';
    }

    // Extract selected preferred name (exclusive checkboxes)
    const primaryPrefNameSelected =
      formData[`${formKey}_primaryPreferredName`] ||
      (primaryProfile.preferredName &&
        isNameSelected(primaryProfile.preferredName));
    const duplicatePrefNameSelected =
      formData[`${formKey}_duplicatePreferredName`] ||
      (duplicateProfile.preferredName &&
        isNameSelected(duplicateProfile.preferredName));

    if (primaryPrefNameSelected) {
      selected.preferredName = primaryProfile.preferredName;
    } else if (duplicatePrefNameSelected) {
      selected.preferredName = duplicateProfile.preferredName;
    }

    // Helper to check if email/phone is selected (handles both unique and shared field names)
    const isContactSelected = (contact, isPhone, originalFieldName) => {
      if (formData[originalFieldName]) return true;

      const value = isPhone
        ? contact.phoneNumber
        : contact.emailAddress.toLowerCase();
      const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '_');
      const sharedFieldName = `${formKey}_shared_${
        isPhone ? 'phone' : 'email'
      }_${sanitizedValue}`;
      return formData[sharedFieldName];
    };

    // Extract selected emails with deduplication
    const selectedEmailAddresses = new Set();
    primaryProfile.emails?.forEach((email, index) => {
      if (
        isContactSelected(email, false, `${formKey}_primaryEmail${index}`) &&
        !selectedEmailAddresses.has(email.emailAddress.toLowerCase())
      ) {
        selected.emails.push({
          emailAddress: email.emailAddress,
          emailType: email.emailType || DEFAULT_EMAIL_TYPE,
          isPrimary: email.isPrimary || false,
          id: email.id || null,
          isOptedOut: email.isOptedOut || false,
          isValidated: email.isValidated || false,
        });
        selectedEmailAddresses.add(email.emailAddress.toLowerCase());
      }
    });
    duplicateProfile.emails?.forEach((email, index) => {
      if (
        isContactSelected(email, false, `${formKey}_duplicateEmail${index}`) &&
        !selectedEmailAddresses.has(email.emailAddress.toLowerCase())
      ) {
        selected.emails.push({
          emailAddress: email.emailAddress,
          emailType: email.emailType || DEFAULT_EMAIL_TYPE,
          isPrimary: email.isPrimary || false,
          id: email.id || null,
          isOptedOut: email.isOptedOut || false,
          isValidated: email.isValidated || false,
        });
        selectedEmailAddresses.add(email.emailAddress.toLowerCase());
      }
    });

    // Extract selected phones with deduplication
    const selectedPhoneNumbers = new Set();
    primaryProfile.phones?.forEach((phone, index) => {
      if (
        isContactSelected(phone, true, `${formKey}_primaryPhone${index}`) &&
        !selectedPhoneNumbers.has(phone.phoneNumber)
      ) {
        selected.phones.push({
          phoneNumber: phone.phoneNumber,
          phoneType: phone.phoneType || DEFAULT_PHONE_TYPE,
          isPrimary: phone.isPrimary || false,
          id: phone.id || null,
          isValidated: phone.isValidated || false,
        });
        selectedPhoneNumbers.add(phone.phoneNumber);
      }
    });
    duplicateProfile.phones?.forEach((phone, index) => {
      if (
        isContactSelected(phone, true, `${formKey}_duplicatePhone${index}`) &&
        !selectedPhoneNumbers.has(phone.phoneNumber)
      ) {
        selected.phones.push({
          phoneNumber: phone.phoneNumber,
          phoneType: phone.phoneType || DEFAULT_PHONE_TYPE,
          isPrimary: phone.isPrimary || false,
          id: phone.id || null,
          isValidated: phone.isValidated || false,
        });
        selectedPhoneNumbers.add(phone.phoneNumber);
      }
    });

    return selected;
  };

  const handleConfirmMerge = (selectedPortalUserId: ?string) => {
    if (!formRef || !currentDuplicateData?.id) {
      toastr.error('Error', 'Form data not available');
      return;
    }

    if (!selectedProfileId) {
      toastr.error('Error', 'No profile selected');
      return;
    }

    const selectedValues = getSelectedValues();

    // Add selectedProspectId and selectedPortalUserId to merge data
    const mergeData = {
      ...selectedValues,
      selectedProspectId: selectedProfileId,
      selectedPortalUserId,
    };

    setShowMergeConfirmModal(false);
    onMerge(currentDuplicateData.id, mergeData);
    onClose();
  };

  const handleCancelMerge = () => {
    setShowMergeConfirmModal(false);
  };

  // Transform backend data for current page
  const currentDuplicateData = useMemo(() => {
    if (!duplicatesData || duplicatesData.length === 0) {
      return null;
    }
    return duplicatesData[currentPage];
  }, [duplicatesData, currentPage]);

  const primaryProfile = useMemo(() => {
    if (!currentDuplicateData) return null;

    const mainProspect = currentDuplicateData.mainProspectData;
    const contact = mainProspect?.contact;

    // Handle phones: new structure (contact.phones) or legacy (phoneNumber)
    const phones =
      contact?.phones?.length > 0
        ? contact.phones
        : mainProspect?.phoneNumber
        ? [{ phoneNumber: mainProspect.phoneNumber }]
        : [];

    // Handle emails: new structure (contact.emails) or legacy (emailAddress)
    const emails =
      contact?.emails?.length > 0
        ? contact.emails
        : mainProspect?.emailAddress
        ? [
            {
              emailAddress: mainProspect.emailAddress,
            },
          ]
        : [];

    return {
      name: `${mainProspect?.firstName || ''} ${
        mainProspect?.lastName || ''
      }`.trim(),
      preferredName: contact?.preferredName || null,
      phones,
      emails,
      additionalOccupants: mainProspect?.additionalOccupants || [],
      household: [],
    };
  }, [currentDuplicateData]);

  const duplicateProfile = useMemo(() => {
    if (!currentDuplicateData) return null;

    const duplicateProspect = currentDuplicateData.duplicateProspectData;
    const contact = duplicateProspect?.contact;

    // Handle phones: new structure (contact.phones) or legacy (phoneNumber)
    const phones =
      contact?.phones?.length > 0
        ? contact.phones
        : duplicateProspect?.phoneNumber
        ? [{ phoneNumber: duplicateProspect.phoneNumber }]
        : [];

    // Handle emails: new structure (contact.emails) or legacy (emailAddress)
    const emails =
      contact?.emails?.length > 0
        ? contact.emails
        : duplicateProspect?.emailAddress
        ? [
            {
              emailAddress: duplicateProspect.emailAddress,
            },
          ]
        : [];

    return {
      name: `${duplicateProspect?.firstName || ''} ${
        duplicateProspect?.lastName || ''
      }`.trim(),
      preferredName: contact?.preferredName || null,
      phones,
      emails,
      additionalOccupants: duplicateProspect?.additionalOccupants || [],
      household: [], // Add household logic if needed
    };
  }, [currentDuplicateData]);

  // Generate unique form key for each duplicate record
  const formKey = currentDuplicateData?.id || `page_${currentPage}`;

  // Check if merge button should be enabled
  const checkMergeButtonEnabled = useCallback(() => {
    if (
      !formRef ||
      !selectedProfileId ||
      !primaryProfile ||
      !duplicateProfile
    ) {
      return false;
    }

    try {
      const formData = formRef.getValues();

      // 1. Check if at least one name is selected
      const hasNameSelected =
        formData[`${formKey}_primaryName`] ||
        formData[`${formKey}_duplicateName`] ||
        (primaryProfile.name === duplicateProfile.name &&
          formData[
            `${formKey}_shared_name_${primaryProfile.name.replace(
              /[^a-zA-Z0-9]/g,
              '_',
            )}`
          ]);

      if (!hasNameSelected) {
        return false;
      }

      // 2. Check if at least one preferred name is selected (if available)
      const hasPrimaryPreferredName = !!primaryProfile.preferredName;
      const hasDuplicatePreferredName = !!duplicateProfile.preferredName;

      if (hasPrimaryPreferredName || hasDuplicatePreferredName) {
        const hasPreferredNameSelected =
          formData[`${formKey}_primaryPreferredName`] ||
          formData[`${formKey}_duplicatePreferredName`] ||
          (primaryProfile.preferredName &&
            duplicateProfile.preferredName &&
            primaryProfile.preferredName === duplicateProfile.preferredName &&
            formData[
              `${formKey}_shared_name_${primaryProfile.preferredName.replace(
                /[^a-zA-Z0-9]/g,
                '_',
              )}`
            ]);

        if (!hasPreferredNameSelected) {
          return false;
        }
      }

      // 3. Check if at least one phone is selected (if available)
      const hasPhones =
        primaryProfile.phones?.length > 0 ||
        duplicateProfile.phones?.length > 0;

      if (hasPhones) {
        let hasPhoneSelected = false;

        // Check primary phones
        primaryProfile.phones?.forEach((phone, index) => {
          if (formData[`${formKey}_primaryPhone${index}`]) {
            hasPhoneSelected = true;
          }
        });

        // Check duplicate phones
        duplicateProfile.phones?.forEach((phone, index) => {
          if (formData[`${formKey}_duplicatePhone${index}`]) {
            hasPhoneSelected = true;
          }
        });

        // Check shared phones
        primaryProfile.phones?.forEach((phone) => {
          const sanitizedPhone = phone.phoneNumber.replace(
            /[^a-zA-Z0-9]/g,
            '_',
          );
          if (formData[`${formKey}_shared_phone_${sanitizedPhone}`]) {
            hasPhoneSelected = true;
          }
        });

        if (!hasPhoneSelected) {
          return false;
        }
      }

      // 4. Check if at least one email is selected (if available)
      const hasEmails =
        primaryProfile.emails?.length > 0 ||
        duplicateProfile.emails?.length > 0;

      if (hasEmails) {
        let hasEmailSelected = false;

        // Check primary emails
        primaryProfile.emails?.forEach((email, index) => {
          if (formData[`${formKey}_primaryEmail${index}`]) {
            hasEmailSelected = true;
          }
        });

        // Check duplicate emails
        duplicateProfile.emails?.forEach((email, index) => {
          if (formData[`${formKey}_duplicateEmail${index}`]) {
            hasEmailSelected = true;
          }
        });

        // Check shared emails
        primaryProfile.emails?.forEach((email) => {
          const sanitizedEmail = email.emailAddress
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, '_');
          if (formData[`${formKey}_shared_email_${sanitizedEmail}`]) {
            hasEmailSelected = true;
          }
        });

        if (!hasEmailSelected) {
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }, [formRef, selectedProfileId, primaryProfile, duplicateProfile, formKey]);

  // Get selected values from form
  const getSelectedValues = () => {
    if (!formRef) {
      return {};
    }

    try {
      const formData = formRef.getValues();
      return extractSelectedValues(formData);
    } catch (error) {
      return {};
    }
  };

  // Reset page when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPage(0);
      setIsProcessing(false); // Reset processing state when modal opens
      setSelectedProfileId(null); // Reset profile selection when modal opens
      setValidationError(null); // Reset validation error when modal opens
      setIsMergeButtonEnabled(false); // Reset button state when modal opens
    } else {
      // Close confirmation modals when main modal closes
      setShowConfirmModal(false);
      setShowMergeConfirmModal(false);
    }
  }, [open]);

  // Watch for form changes and update merge button state
  useEffect(() => {
    if (!formRef || !open) {
      setIsMergeButtonEnabled(false);
      return;
    }

    // Initial check
    setIsMergeButtonEnabled(checkMergeButtonEnabled());

    // Subscribe to form changes
    const subscription = formRef.watch(() => {
      setIsMergeButtonEnabled(checkMergeButtonEnabled());
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, [
    formRef,
    selectedProfileId,
    formKey,
    primaryProfile,
    duplicateProfile,
    open,
    checkMergeButtonEnabled,
  ]);

  if (!duplicatesData || duplicatesData.length === 0) {
    return null;
  }

  return (
    <>
      <Modal
        actionsProps={[
          {
            children: intl.formatMessage(messages.cancelButton),
            variant: 'text',
            onClick: onClose,
          },
          {
            startIcon: <CloseIcon fontSize="medium" color="primary" />,
            children: intl.formatMessage(messages.notAMatchButton),
            variant: 'text',
            onClick: handleNotAMatch,
            disabled: isProcessing,
            isSubmitting: isProcessing,
          },
          {
            children: intl.formatMessage(messages.mergeContactButton),
            onClick: handleConfirm,
            disabled: !isMergeButtonEnabled || isProcessing,
            isSubmitting: isProcessing,
          },
        ]}
        icon={<PeopleIcon />}
        onClose={onClose}
        open={open}
        title={intl.formatMessage(messages.modalTitle)}
        ModalProps={{
          PaperProps: {
            sx: {
              maxWidth: 720,
            },
          },
        }}
      >
        <Typography variant="bodyMedium">
          {intl.formatMessage(messages.modalDescription)}
        </Typography>
        <Divider
          orientation="horizontal"
          textAlign="center"
          variant="fullWidth"
          sx={{
            marginTop: 2,
          }}
        />
        <Grid container padding={2}>
          {validationError ? (
            <Banner
              text={validationError}
              icon={<AlertIcon color="inherit" />}
              hasBorder={false}
              color="red"
              TypographyProps={{
                variant: 'bodySmall',
                sx: {
                  textWrap: 'wrap',
                },
              }}
            />
          ) : (
            <Banner
              text={intl.formatMessage(messages.bannerText)}
              icon={<AlertInfoIcon color="inherit" />}
              hasBorder={false}
              color="blue"
              TypographyProps={{
                variant: 'bodySmall',
                sx: {
                  textWrap: 'wrap',
                },
              }}
            />
          )}

          <DuplicatesForm
            currentDuplicateData={currentDuplicateData}
            primaryProfile={primaryProfile}
            duplicateProfile={duplicateProfile}
            intl={intl}
            formKey={formKey}
            onFormReady={(methods) => setFormRef(methods)}
            selectedProfileId={selectedProfileId}
            onProfileSelect={handleProfileSelect}
            portalUsers={currentDuplicateData?.portalUsers || []}
          />

          {/* Pagination Controls */}
          {duplicatesData.length > 1 && (
            <Grid container justifyContent="center" sx={{ paddingY: 2 }}>
              <Grid item>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                >
                  <Button
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                    variant="text"
                    size="small"
                    color="inherit"
                  >
                    <LessThanIcon />
                  </Button>

                  <Typography variant="labelLarge">
                    {`${currentPage + 1}/${duplicatesData.length}`}
                  </Typography>

                  <Button
                    onClick={handleNextPage}
                    disabled={currentPage >= duplicatesData.length - 1}
                    variant="text"
                    size="small"
                    color="inherit"
                  >
                    <GreaterThanIcon />
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Modal>

      <ConfirmRemovalModal
        open={showConfirmModal}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirmRemoval}
        intl={intl}
      />

      <ConfirmMergeModal
        open={showMergeConfirmModal}
        onClose={handleCancelMerge}
        onConfirm={handleConfirmMerge}
        intl={intl}
        selectedValues={getSelectedValues()}
        contactName={
          getSelectedValues()?.firstName && getSelectedValues()?.lastName
            ? `${getSelectedValues().firstName} ${
                getSelectedValues().lastName
              }`.trim()
            : primaryProfile?.name ||
              duplicateProfile?.name ||
              'Unknown Contact'
        }
        selectedProfileId={selectedProfileId}
        primaryProfile={primaryProfile}
        duplicateProfile={duplicateProfile}
        currentDuplicateData={currentDuplicateData}
      />
    </>
  );
};

export default DuplicatesModal;
